import type { FC } from 'react'
import { useEffect, useState } from 'react'

import type { WrappedComponentProps } from 'react-intl'
import { injectIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import { useAppContext } from '@/App.context'
import {
  STEP_ACCESSORIES,
  STEP_ACCESSORIES_ALL,
  STEP_CONFIRM,
  STEP_KEYBOARD_MOUSE,
  STEP_MACHINE,
  STEP_MONITOR,
  STEP_SOFTWARE,
  STEP_SUMMARY,
  STEP_WORK_TYPE,
} from '@/ducks/onboarding/constants'
import { goToStep, loadProducts, resetOrder } from '@/ducks/onboarding/onboardingActions'
import {
  displayInitialSplash,
  getCurrentStep,
  getFurthestStep,
  getOrderId,
  getOrderSum,
  getSteps,
} from '@/ducks/onboarding/onboardingSelectors'
import useUser from '@/hooks/user'
import { numberFormat } from '@/util/formatters/numbers'
import { OnboardingCostIcon, OnboardingRestartIcon } from '@/util/icons'

import { Translation } from '../i18n/Translation'

import InitialSplash from './InitialSplash'
import StepAccessories from './steps/StepAccessories'
import StepAccessoriesAll from './steps/StepAccessoriesAll'
import StepConfirm from './steps/StepConfirm'
import StepMachine from './steps/StepMachine'
import StepScreen from './steps/StepMonitor'
import StepMouseKeyboard from './steps/StepMouseKeyboard'
import StepPlaceholder from './steps/StepPlaceholder'
import StepSoftware from './steps/StepSoftware'
import StepSummary from './steps/StepSummary'
import StepWorkType from './steps/StepWorkType'
import type { Step } from './types'

import './onboarding.scss'

// deprecated
const Onboarding: FC<WrappedComponentProps> = ({ intl }) => {
  const [isProgressBarExtended, setIsProgressBarExtended] = useState(false)

  const dispatch = useDispatch()
  const user = useUser()

  const currentStep: Step = useSelector(getCurrentStep)
  const furthestStep: number = useSelector(getFurthestStep)
  const steps: Step[] = useSelector(getSteps)
  const orderSum: number = useSelector(getOrderSum)
  const orderId: number = useSelector(getOrderId)
  const isInitialSplashDisplayed: boolean = useSelector(displayInitialSplash)

  useEffect(() => {
    if (!user) return

    // @ts-ignore
    dispatch(loadProducts())
  }, [])

  const { formatMessage } = intl

  if (isInitialSplashDisplayed && user?.isManager) {
    return (
      <div id="onboarding" className="onboarding">
        <InitialSplash />
      </div>
    )
  }

  return (
    <div id="onboarding" className="onboarding">
      <div className="content">
        <h2>
          <Translation id="Bestill PC" />
        </h2>

        <div className={`progress-bar ${isProgressBarExtended ? 'extended' : 'collapsed'}`}>
          {steps.map((step, index) => (
            <ProgressBarStep
              step={step}
              key={step}
              isCurrentStep={step === currentStep}
              isActive={index <= furthestStep}
              isLastActive={index === furthestStep}
              isClosed={!!orderId}
              goToStep={!orderId ? step => dispatch(goToStep(step)) : () => {}}
              toggle={!orderId ? () => setIsProgressBarExtended(!isProgressBarExtended) : () => {}}
              collapse={() => setIsProgressBarExtended(false)}
            />
          ))}
        </div>

        <div
          className={`order-status ${orderSum > 0 ? 'active' : 'inactive'}`}
          title={formatMessage({ id: 'Totalkost for ordre' })}
        >
          <OnboardingCostIcon /> {numberFormat(orderSum)},-
          <button
            onClick={() => dispatch(resetOrder())}
            title={formatMessage({ id: orderId ? 'Ny ordre' : 'Resett ordre' })}
          >
            <OnboardingRestartIcon />
          </button>
        </div>

        <CurrentStep currentStep={currentStep} />
      </div>
    </div>
  )
}

const ProgressBarStep: FC<{
  step: Step
  isActive: boolean
  isLastActive: boolean
  isCurrentStep: boolean
  isClosed: boolean
  goToStep: (step: Step) => void
  collapse: () => void
  toggle: () => void
}> = ({ step, isActive, isLastActive, isCurrentStep, isClosed, goToStep, collapse, toggle }) => {
  const { whiteLabel } = useAppContext()

  const onClick = () => {
    if (isCurrentStep) {
      toggle()
      return
    }
    if (isActive) {
      goToStep(step)
      collapse()
    }
  }
  return (
    <button
      onClick={onClick}
      className={`progress-bar__step ${isActive ? 'active' : 'inactive'} ${
        isCurrentStep && 'current'
      } ${isLastActive && 'last'} ${isClosed && 'closed'} ${whiteLabel}`}
    >
      <Translation id={step} />
    </button>
  )
}

const CurrentStep: FC<{ currentStep: Step }> = ({ currentStep }) => {
  switch (currentStep) {
    case STEP_WORK_TYPE:
      // @ts-ignore
      return <StepWorkType />
    case STEP_MACHINE:
      return <StepMachine />
    case STEP_SOFTWARE:
      return <StepSoftware />
    case STEP_MONITOR:
      return <StepScreen />
    case STEP_KEYBOARD_MOUSE:
      return <StepMouseKeyboard />
    case STEP_ACCESSORIES:
      return <StepAccessories />
    case STEP_ACCESSORIES_ALL:
      return <StepAccessoriesAll />
    case STEP_CONFIRM:
      return <StepConfirm />
    case STEP_SUMMARY:
      return <StepSummary />
    default:
      return <StepPlaceholder />
  }
}

export default injectIntl(Onboarding)
